import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";

import { useGlobalSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { availableCustomersAtom } from "~/shared/state/user-profile-state.ts";

import { ActiveProfileDisplay } from "./active-profile-display.tsx";
import styles from "./global-header.module.css";
import { ProfileSwitcher } from "./profile-switcher.tsx";

const MOBILE_BREAKPOINT = 992;

export function GlobalHeader() {
	const [isOpen, setIsOpen] = useState(false);
	const isMobile = useIsMobile(MOBILE_BREAKPOINT);
	const availableCustomers = useAtomValue(availableCustomersAtom);
	const hasMultipleCustomers = availableCustomers.length > 1;
	const containerRef = useRef<HTMLDivElement>(null);
	const { isLoading } = useGlobalSpinner();

	const handleToggle = useCallback(() => {
		setIsOpen((prev) => !prev);
	}, []);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	useEffect(() => {
		if (!isOpen) return;

		const handleMouseDown = (event: MouseEvent) => {
			// Keep the switcher mounted while a global spinner is active (e.g. an
			// in-flight profile save). The save and its result handling — including
			// the error modal — live in ProfileSwitcher, so unmounting it mid-save
			// would tear down the loading overlay and drop the error feedback.
			if (isLoading) return;
			const target = event.target;
			if (!(target instanceof Node)) return;
			if (containerRef.current?.contains(target)) return;
			// Ignore clicks inside the Kendo Dialog portal (e.g. confirmation modal)
			if (
				target instanceof Element &&
				target.closest(".k-dialog-wrapper, .k-animation-container")
			) {
				return;
			}
			setIsOpen(false);
		};

		document.addEventListener("mousedown", handleMouseDown);
		return () => {
			document.removeEventListener("mousedown", handleMouseDown);
		};
	}, [isOpen, isLoading]);

	if (isMobile || !hasMultipleCustomers) {
		return null;
	}

	return (
		<header
			aria-label={"Global site header"}
			className={styles.globalHeader}
			data-testid={"global-header"}
		>
			<div ref={containerRef} className={styles.profileSwitcherContainer}>
				<ActiveProfileDisplay onToggle={handleToggle} />
				{isOpen ? <ProfileSwitcher onClose={handleClose} /> : null}
			</div>
		</header>
	);
}
